import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
} from 'react-native';
import Svg, { Circle } from 'react-native-svg';

// Legend dot icon
const LegendDot = ({ color }: { color: string }) => (
  <Svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <Circle cx="6" cy="6" r="6" fill={color} />
  </Svg>
);

// Data interfaces
export interface HorizontalChartDataPoint {
  label: string;
  value: number;
  color: string;
}

export interface DLOHorizontalBarChartProps {
  title?: string;
  data: HorizontalChartDataPoint[];
  targetValue?: number;
  targetLabel?: string;
  minValue?: number;
  maxValue?: number;
  style?: any;
}

const DLOHorizontalBarChart: React.FC<DLOHorizontalBarChartProps> = ({
  title = 'Efficiency Metrics',
  data,
  targetValue = 49,
  targetLabel = 'Target KCPH',
  minValue = 44,
  maxValue = 55,
  style,
}) => {
  // Responsive base (used before layout captured)
  const screenWidth = Dimensions.get('window').width;
  const isSmallScreen = screenWidth < 768;
  const isMediumScreen = screenWidth >= 768 && screenWidth < 1024;
  const baseFontSize = 12;
  const baseTitleFontSize = isSmallScreen ? 14 : isMediumScreen ? 15 : 16;
  const baseBarHeight = 32;

  // Layout state to allow full parent fill
  const [layout, setLayout] = useState({ width: 0, height: 0 });
  
  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    value: number;
    x: number;
    y: number;
    barIndex: number;
  } | null>(null);

  // Handle empty data gracefully
  const effectiveFontSize = baseFontSize;
  const effectiveTitleFontSize = baseTitleFontSize;
  const barHeight = baseBarHeight;

  // Early empty state – still fill parent flexibly
  if (!data || data.length === 0) {
    return (
      <View
        style={[styles.fillWrapper, style]}
        onLayout={e => {
          const { width, height } = e.nativeEvent.layout;
          if (width !== layout.width || height !== layout.height) setLayout({ width, height });
        }}
      >
        <View style={styles.container}>
          <View style={styles.header}>
            <Text style={[styles.title, { fontSize: effectiveTitleFontSize }]}>{title}</Text>
          </View>
          <View style={styles.emptyState}>
            <Text style={[styles.emptyStateText, { fontSize: effectiveFontSize }]}>No data available</Text>
          </View>
        </View>
      </View>
    );
  }

  // Layout-based sizing (fallback widths until layout known)
  const fallbackWidth = Math.min(screenWidth - (isSmallScreen ? 32 : 96), 640);
  const containerWidth = layout.width || fallbackWidth;
  const containerHeight = layout.height || 360;

  // Margins / paddings
  const chartAreaLeft = isSmallScreen ? 80 : 90;
  const rightPadding = 24;
  const headerHeight = 70; // approx title space
  const legendHeight = 28; // legend space
  const xAxisHeight = 28; // x-axis labels block
  const verticalPadding = 16; // buffer

  // Bars geometry
  const totalBars = data.length;
  const barSpacing = 34;
  const naturalChartHeight = (totalBars * barHeight) + ((totalBars - 1) * barSpacing);
  const availableForChart = containerHeight - headerHeight - legendHeight - xAxisHeight - verticalPadding;
  const chartAreaHeight = Math.max(naturalChartHeight, availableForChart > 0 ? availableForChart : naturalChartHeight);
  const chartAreaTop = headerHeight; // start below header
  const chartAreaWidth = containerWidth - chartAreaLeft - rightPadding;
  const dynamicContainerHeight = containerHeight; // fill parent, no manual calc

  // Calculate scale for bar widths
  const valueRange = maxValue - minValue;
  const getBarWidth = (value: number) => {
    const normalizedValue = (value - minValue) / valueRange;
    return Math.max(0, Math.min(1, normalizedValue)) * chartAreaWidth;
  };


  // Generate X-axis labels with whole numbers and nice intervals
  function getNiceStep(min: number, max: number, maxTicks = 8) {
    // Calculate a nice step size for the axis
    const range = max - min;
    if (range <= 0) return 1;
    const roughStep = range / maxTicks;
    // Find the nearest power of 10
    const pow10 = Math.pow(10, Math.floor(Math.log10(roughStep)));
    let step = pow10;
    if (roughStep / pow10 > 5) step = pow10 * 5;
    else if (roughStep / pow10 > 2) step = pow10 * 2;
    return Math.max(1, Math.round(step));
  }

  const xAxisStep = getNiceStep(minValue, maxValue);
  const xAxisLabels = [];
  for (let i = Math.ceil(minValue); i <= Math.floor(maxValue); i += xAxisStep) {
    xAxisLabels.push(i);
  }
  // Always include maxValue as last label if not already present
  if (xAxisLabels.length === 0 || xAxisLabels[xAxisLabels.length - 1] < Math.floor(maxValue)) {
    xAxisLabels.push(Math.floor(maxValue));
  }

  // Calculate target line position
  const targetLineX = getBarWidth(targetValue);

  // Handle mouse events for tooltip
  const handleMouseMove = (event: any, dataPoint: HorizontalChartDataPoint, barIndex: number) => {
    if (Platform.OS !== 'web') return;
    
    const nativeEvent = event.nativeEvent;
    const rect = nativeEvent.target.getBoundingClientRect();
    const x = nativeEvent.clientX - rect.left;
    const barTop = (barIndex * (barHeight + barSpacing)) + (barSpacing / 2);
    
    setTooltip({
      visible: true,
      value: dataPoint.value,
      x: x,
      y: barTop - 40, // Position above the bar
      barIndex,
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  return (
    <View
      style={[styles.fillWrapper, style]}
      onLayout={e => {
        const { width, height } = e.nativeEvent.layout;
        if (width !== layout.width || height !== layout.height) setLayout({ width, height });
      }}
    >
      <View style={[styles.container, { width: containerWidth, height: dynamicContainerHeight }]}>      
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.title, { fontSize: effectiveTitleFontSize }]}>{title}</Text>
      </View>

      {/* Chart Container */}
      <View style={styles.chartWrapper}>
        {/* Y-Axis Labels */}
        <View style={[styles.yAxisContainer, { width: chartAreaLeft }]}>          
          {data.map((dataPoint, index) => {
            // Align labels with bars that are positioned between grid lines
            const barTop = (index * (barHeight + barSpacing)) + (barSpacing / 2);
            return (
              <View
                key={dataPoint.label}
                style={[
                  styles.yAxisLabelWrapper,
                  {
                    top: chartAreaTop + barTop + (barHeight / 2) - 8,
                    right: isSmallScreen ? 8 : 16,
                  },
                ]}
              >
                <Text style={[styles.yAxisLabel, { fontSize: effectiveFontSize }]}>{dataPoint.label}</Text>
              </View>
            );
          })}
        </View>

        {/* Chart Area with Grid */}
        <View style={[styles.chartArea, { 
          left: chartAreaLeft, 
          top: chartAreaTop, 
          width: chartAreaWidth, 
          height: 200 
        }]}>
          {/* Vertical Grid Lines */}
          {xAxisLabels.map((value, index) => {
            const lineX = getBarWidth(value);
            return (
              <View
                key={`grid-${value}`}
                style={[
                  styles.verticalGridLine,
                  {
                    left: index === 0 ? 0 : lineX,
                    height: '100%',
                  },
                ]}
              />
            );
          })}

          {/* Horizontal Grid Lines for bar separation */}
          {Array.from({ length: data.length + 1 }, (_, index) => (
            <View
              key={`hgrid-${index}`}
              style={[
                styles.horizontalGridLine,
                {
                  top: index * (barHeight + barSpacing),
                  width: chartAreaWidth,
                },
              ]}
            />
          ))}

          {/* Data Bars */}
          <View style={styles.barsContainer}>
            {data.map((dataPoint, index) => {
              const width = getBarWidth(dataPoint.value);
              // Position bars between grid lines by adding half the spacing
              const top = (index * (barHeight + barSpacing)) + (barSpacing / 2);
              return (
                <View
                  key={dataPoint.label}
                  style={[
                    styles.bar,
                    {
                      top,
                      width,
                      height: barHeight,
                      backgroundColor: dataPoint.color,
                    },
                  ]}
                  {...(Platform.OS === 'web' && {
                    onMouseMove: (event: any) => handleMouseMove(event, dataPoint, index),
                    onMouseLeave: handleMouseLeave,
                  })}
                />
              );
            })}
          </View>

          {/* Target Line */}
          <View
            style={[
              styles.targetLine,
              {
                left: targetLineX - 1,
                // Height should span from center of first bar to center of last bar
                height: (data.length - 1) * (barHeight + barSpacing),
                // Start from the center of the first bar
                top: (barSpacing / 2) + (barHeight / 2),
              },
            ]}
          />

          {/* Target Indicators */}
          {data.map((_, index) => {
            // Use same positioning logic as bars to ensure alignment
            const barTop = (index * (barHeight + barSpacing)) + (barSpacing / 2);
            const indicatorTop = barTop + (barHeight / 2) - 5; // center 10px indicator vertically on bar
            return (
              <View
                key={`target-${index}`}
                style={[
                  styles.targetIndicator,
                  {
                    left: targetLineX - 5,
                    top: indicatorTop,
                  },
                ]}
              />
            );
          })}
        </View>

        {/* Sliding Tooltip */}
        {tooltip && Platform.OS === 'web' && (
          <View
            style={[
              styles.slidingTooltip,
              {
                left: Math.max(chartAreaLeft, Math.min(chartAreaLeft + tooltip.x - 20, chartAreaLeft + chartAreaWidth - 40)), // Keep within chart bounds
                top: chartAreaTop + tooltip.y,
                opacity: tooltip.visible ? 1 : 0,
              },
            ]}
          >
            <Text style={styles.tooltipText}>{tooltip.value.toFixed(1)}</Text>
            {/* Tooltip Arrow */}
            <View style={styles.tooltipArrow} />
          </View>
        )}

        {/* X-Axis Labels */}
        <View
          style={[
            styles.xAxisContainer,
            {
              left: chartAreaLeft,
              top: chartAreaTop + chartAreaHeight + 8,
              width: chartAreaWidth,
            },
          ]}
        >
          {xAxisLabels.map(value => {
            const x = getBarWidth(value);
            return (
              <View
                key={value}
                style={[
                  styles.xAxisLabelWrapper,
                  { left: value === minValue ? 0 : x - 6 },
                ]}
              >
                <Text style={[styles.xAxisLabel, { fontSize: effectiveFontSize }]}>{Math.round(value)}</Text>
              </View>
            );
          })}
        </View>
      </View>

      {/* Legend */}
      <View
        style={[
          styles.legendContainer,
          isSmallScreen && styles.legendContainerSmall,
          { bottom: 12 },
        ]}
      >
        <View style={styles.legendItem}>
          <LegendDot color="#D40020" />
          <Text style={[styles.legendLabel, { fontSize: effectiveFontSize, marginLeft: 6 }]}>{targetLabel}</Text>
        </View>
      </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  fillWrapper: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  container: {
    backgroundColor: '#FBFCFE',
    borderRadius: 8,
    shadowColor: 'rgba(0, 92, 188, 0.08)',
    shadowOffset: { width: 4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 4,
    position: 'relative',
    maxWidth: '100%',
    height: '100%',
  },
  header: {
    position: 'absolute',
    left: 24,
    top: 24,
    right: 24,
  },
  title: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 22,
  },
  chartWrapper: {
    position: 'relative',
    flex: 1,
  },
  yAxisContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
  },
  yAxisLabelWrapper: {
    position: 'absolute',
    alignItems: 'flex-end',
    justifyContent: 'center',
    height: 16,
  },
  yAxisLabel: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    textAlign: 'right',
  },
  chartArea: {
    position: 'absolute',
    backgroundColor: 'transparent',
  },
  verticalGridLine: {
    position: 'absolute',
    width: 1,
    backgroundColor: '#DFDFDF',
    top: 0,
  },
  horizontalGridLine: {
    position: 'absolute',
    height: 1,
    backgroundColor: '#DFDFDF',
  },
  barsContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  bar: {
    position: 'absolute',
    left: 0,
  },
  targetLine: {
    position: 'absolute',
    width: 2,
    backgroundColor: '#D40020',
    top: 0,
  },
  targetIndicator: {
    position: 'absolute',
    width: 10,
    height: 10,
    backgroundColor: '#D40020',
  },
  xAxisContainer: {
    position: 'absolute',
    height: 22,
  },
  xAxisLabelWrapper: {
    position: 'absolute',
    width: 12,
    alignItems: 'center',
    justifyContent: 'center',
    height: 22,
  },
  xAxisLabel: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
  legendContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  legendContainerSmall: {
    left: '50%',
    marginLeft: -45,
    width: 90,
    bottom: 15,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  legendLabel: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 16,
    fontWeight: '400',
    textAlign: 'center',
  },
  slidingTooltip: {
    position: 'absolute',
    backgroundColor: '#272727',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1000,
  },
  tooltipText: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter-Medium',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },
  tooltipArrow: {
    position: 'absolute',
    bottom: -4,
    left: '50%',
    marginLeft: -4,
    width: 0,
    height: 0,
    borderLeftWidth: 4,
    borderRightWidth: 4,
    borderTopWidth: 4,
    borderStyle: 'solid',
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: '#272727',
  },
});

export default DLOHorizontalBarChart;
